import React, { useState, useEffect, useRef } from 'react'
import { Lock, Eye, EyeOff } from 'lucide-react'

// Declare Turnstile types
declare global {
  interface Window {
    turnstile: {
      render: (element: HTMLElement, options: any) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}

interface AuthGateProps {
  onAuthenticated: () => void
}

const AuthGate: React.FC<AuthGateProps> = ({ onAuthenticated }) => {
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState('')
  const turnstileRef = useRef<HTMLDivElement>(null)
  const turnstileWidgetId = useRef<string | null>(null)

  // Check if already authenticated
  useEffect(() => {
    const isAuth = localStorage.getItem('grace-staff-authenticated')
    if (isAuth === 'true') {
      onAuthenticated()
    }
  }, [onAuthenticated])

  // Initialize Turnstile
  useEffect(() => {
    const initTurnstile = () => {
      if (window.turnstile && turnstileRef.current && !turnstileWidgetId.current) {
        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: '0x4AAAAAAB8F4nMruyyKRcpI',
          callback: (token: string) => {
            setTurnstileToken(token)
          },
          'error-callback': () => {
            setTurnstileToken('')
            setError('Turnstile verification failed. Please try again.')
          },
          'expired-callback': () => {
            setTurnstileToken('')
          }
        })
      }
    }

    // Wait for Turnstile to load
    if (window.turnstile) {
      initTurnstile()
    } else {
      const checkTurnstile = setInterval(() => {
        if (window.turnstile) {
          initTurnstile()
          clearInterval(checkTurnstile)
        }
      }, 100)

      return () => clearInterval(checkTurnstile)
    }

    return () => {
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current)
      }
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    // Check Turnstile verification
    if (!turnstileToken) {
      setError('Please complete the security verification.')
      setIsLoading(false)
      return
    }

    // Get password from localStorage or use default
    const correctPassword = localStorage.getItem('grace-staff-password') || 'grace2025'

    if (password === correctPassword) {
      localStorage.setItem('grace-staff-authenticated', 'true')
      onAuthenticated()
    } else {
      setError('Incorrect password. Please try again.')
      setPassword('')
      // Reset Turnstile on failed login
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId.current)
        setTurnstileToken('')
      }
    }

    setIsLoading(false)
  }


  return (
    <div className="min-h-screen bg-gradient-to-br from-grace-blue-600 to-grace-blue-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4">
            <img 
              src="https://thechurchco-production.s3.amazonaws.com/uploads/sites/1824/2020/02/Grace-Fellowship-Ministeries-Logo-2.png"
              alt="Grace Fellowship Ministries"
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Grace Fellowship Staff Portal
          </h1>
          <p className="text-gray-600">
            Enter the staff password to access your dashboard
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Staff Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-grace-blue-500 focus:border-transparent"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Turnstile Widget */}
          <div className="flex justify-center">
            <div ref={turnstileRef}></div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-grace-blue-600 hover:bg-grace-blue-700 disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <Lock className="w-5 h-5 mr-2" />
                Access Dashboard
              </>
            )}
          </button>
        </form>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="text-center">
            <p className="text-xs text-gray-500 mb-2">
              Need help accessing the portal?
            </p>
            <a
              href="mailto:jeremy@gracefm.org"
              className="text-sm text-grace-blue-600 hover:text-grace-blue-700 underline"
            >
              Contact IT Support
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AuthGate 